/**
 * Strutture Feature React Query Hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/lib/useAuth';
import * as api from '@/api/queries/strutture';
import type { Struttura, StrutturaInsert, StrutturaUpdate, Camera, CameraInsert, CameraUpdate } from '@/types';

const QUERY_KEY = 'strutture';
const CAMERE_KEY = 'camere';

// ============================================================
// QUERIES
// ============================================================

export function useStrutture() {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: [QUERY_KEY, user?.id],
    queryFn: async () => {
      const { data, error } = await api.getStrutture({ userId: user?.id || '' });
      if (error) throw error;
      return (data || []) as Struttura[];
    },
    enabled: !!user?.id,
  });
}

export function useStruttura(id?: string) {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: [QUERY_KEY, id, user?.id],
    queryFn: async () => {
      if (!id) return null;
      const { data, error } = await api.getStrutturaById(id, user?.id);
      if (error) throw error;
      return data as Struttura | null;
    },
    enabled: !!id && !!user?.id,
  });
}

export function useStruttureAttive() {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: [QUERY_KEY, 'attive', user?.id],
    queryFn: async () => {
      const { data, error } = await api.getStruttureAttive();
      if (error) throw error;
      return (data || []) as Struttura[];
    },
    enabled: !!user?.id,
  });
}

// ============================================================
// MUTATIONS
// ============================================================

export function useCreateStruttura() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (struttura: StrutturaInsert) => {
      const { data, error } = await api.createStruttura({ ...struttura, user_id: user?.id });
      if (error) throw error;
      return data as Struttura;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

export function useUpdateStruttura() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: StrutturaUpdate }) => {
      const { data: result, error } = await api.updateStruttura(id, data, user?.id);
      if (error) throw error;
      return result as Struttura;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, variables.id] });
    },
  });
}

export function useDeleteStruttura() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await api.deleteStruttura(id, user?.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

// ============================================================
// STATS
// ============================================================

export function useStruttureStats() {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: [QUERY_KEY, 'stats', user?.id],
    queryFn: async () => {
      const { data: strutture, error } = await api.getStrutture({ userId: user?.id });
      if (error) throw error;
      const s = strutture || [];
      return {
        totali: s.length,
        attive: s.filter((x: Struttura) => x.stato === 'Attiva').length,
        inattive: s.filter((x: Struttura) => x.stato === 'Inattiva').length,
        manutenzione: s.filter((x: Struttura) => x.stato === 'In manutenzione').length,
      };
    },
    enabled: !!user?.id,
  });
}

// ============================================================
// CAMERE QUERIES
// ============================================================

export function useCamereByStruttura(strutturaId?: string) {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: [CAMERE_KEY, strutturaId, user?.id],
    queryFn: async () => {
      if (!strutturaId) {
        // Se non c'è strutturaId, carica tutte le camere dell'utente
        const { data: strutture, error: struttureError } = await api.getStrutture({ userId: user?.id });
        if (struttureError) throw struttureError;
        
        let allCamere: Camera[] = [];
        for (const s of strutture || []) {
          const { data, error } = await api.getCamereByStruttura(s.id, user?.id);
          if (error) throw error;
          allCamere = [...allCamere, ...(data || []).map((c: Camera) => ({ ...c, struttura_nome: s.nome }))];
        }
        return allCamere;
      }
      const { data, error } = await api.getCamereByStruttura(strutturaId, user?.id);
      if (error) throw error;
      return (data || []) as Camera[];
    },
    enabled: !!user?.id,
  });
}

export function useCamera(id?: string) {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: [CAMERE_KEY, id, user?.id],
    queryFn: async () => {
      if (!id) return null;
      const { data, error } = await api.getCamereByStruttura(id, user?.id);
      if (error) throw error;
      return (data?.[0] || null) as Camera | null;
    },
    enabled: !!user?.id && !!id,
  });
}

export function useCreateCamera() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (camera: CameraInsert) => {
      const { data, error } = await api.createCamera(camera, user?.id);
      if (error) throw error;
      return data as Camera;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CAMERE_KEY] });
    },
  });
}

export function useUpdateCamera() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: CameraUpdate }) => {
      const { data: result, error } = await api.updateCamera(id, data, user?.id);
      if (error) throw error;
      return result as Camera;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [CAMERE_KEY] });
      queryClient.invalidateQueries({ queryKey: [CAMERE_KEY, variables.id] });
    },
  });
}

export function useDeleteCamera() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await api.deleteCamera(id, user?.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CAMERE_KEY] });
    },
  });
}

// ============================================================
// ENTERPRISE HOOKS (nuovo schema admin_id)
// ============================================================

const PROPERTIES_KEY = 'properties';

export function useProperties(adminId?: string) {
  return useQuery({
    queryKey: [PROPERTIES_KEY, adminId],
    queryFn: async () => {
      if (!adminId) return [];
      // Usa l'API strutture per ora (fino a migration completa)
      const { data, error } = await api.getStrutture({ userId: adminId });
      if (error) throw error;
      return (data || []) as Struttura[];
    },
    enabled: !!adminId,
  });
}
